import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const CHAPTERS = [
  {
    num: "01",
    title: "Screen Time with Soul",
    body: "DreamWeaver replaces mindless scrolling with a meaningful 20-minute ritual. Every story is calm, beautiful, and crafted to ease children into sleep — not stimulate them.",
    icon: "✦",
  },
  {
    num: "02",
    title: "The Magic of Being the Hero",
    body: "When children hear their own name woven through a magical adventure, something incredible happens: they become more confident, more imaginative, and genuinely excited for bedtime.",
    icon: "◈",
  },
  {
    num: "03",
    title: "Soothing 20-Minute Read-Aloud Ritual",
    body: "Every story is precisely engineered for a 20-minute read-aloud. Pacing slows as the tale winds down, with sleepy cues and gentle language that naturally guides children toward sleep.",
    icon: "⬡",
  },
  {
    num: "04",
    title: "Heirloom Illustrated Keepsakes",
    body: "Each story comes alive with 10–14 original illustrations in your chosen art style — watercolor, cartoon, or pastel — starring your child. These aren't stock images. They're unique artworks created just for this story.",
    icon: "◇",
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function ManifestoSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="manifesto" data-testid="manifesto-section" className="py-24 lg:py-36 relative">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(109,40,217,0.06) 0%, transparent 70%)"
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-block text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80 mb-4"
          >
            Our Manifesto
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-snug"
          >
            Why bedtime stories<br className="hidden sm:block" />{" "}
            <span className="gradient-text">should feel like this</span>
          </motion.h2>
        </div>

        {/* Chapters */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {CHAPTERS.map((chapter, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              className="group glass-card rounded-2xl p-8 cursor-default border border-purple-500/10 hover:border-purple-500/30 transition-colors duration-300"
            >
              {/* Chapter number */}
              <div className="flex items-start gap-4 mb-5">
                <span className="font-mono text-xs text-purple-400/60 tracking-widest mt-1">{chapter.num}</span>
                <span className="text-2xl text-amber-400/60 group-hover:text-amber-400 transition-colors">{chapter.icon}</span>
              </div>

              <h3 className="font-serif text-xl font-bold text-white mb-3 group-hover:text-amber-100 transition-colors">
                {chapter.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                {chapter.body}
              </p>

              {/* Bottom accent line */}
              <div className="mt-6 h-px bg-gradient-to-r from-purple-500/30 via-amber-500/20 to-transparent" />
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom quote */}
        <motion.blockquote
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-20 text-center max-w-2xl mx-auto"
        >
          <p className="font-serif text-xl lg:text-2xl text-slate-300 italic leading-relaxed">
            "You are a master of wonder. Every story should feel like it was crafted just for that one child, on that one night, under those exact stars."
          </p>
          <cite className="mt-4 block text-xs font-mono text-amber-300/60 tracking-widest uppercase not-italic">
            — The DreamWeaver Promise
          </cite>
        </motion.blockquote>
      </div>
    </section>
  );
}
